import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Check, Star, BadgeCheck, Inbox, Settings, CreditCard, Pencil, Save,
  Mail, Phone, MapPin, Sparkles, ArrowUpRight, PhoneCall,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATES, CHARGE_TYPES } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PLANS = [
  { tier: "basic", name: "Basic", monthly: 50, annual: 500, perks: ["Live directory listing", "Receive client leads", "Up to 3 practice-area tags"] },
  { tier: "pro", name: "Pro", monthly: 100, annual: 1000, perks: ["Featured placement + highlighted card", "Priority in state searches", "Unlimited practice-area tags", "Everything in Basic"] },
  { tier: "elite", name: "Elite", monthly: 250, annual: 2500, perks: ["Top placement above Pro", "Verified badge", "Highest search priority", "Everything in Pro"] },
];

export default function AttorneyDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [att, setAtt] = useState(null);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [action, setAction] = useState("");
  const [billing, setBilling] = useState("monthly");
  const unread = leads.filter((l) => !l.contacted).length;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [m, l] = await Promise.all([api.get("/attorney/me"), api.get("/attorney/leads")]);
      setAtt(m.data);
      setForm(m.data);
      setLeads(l.data);
    } catch { toast.error("Could not load your practice"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (user?.role === "attorney") {
      load();
      if (new URLSearchParams(window.location.search).get("sub") === "success") {
        toast.success("Subscription active — welcome aboard!");
        window.history.replaceState(null, "", "/attorney");
      }
    }
  }, [user, load]);

  if (loading || user === null) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-forest-700" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "attorney") return <Navigate to="/dashboard" replace />;

  const subscribe = async (tier) => {
    setAction(tier);
    try {
      const { data } = await api.post("/attorney/subscribe", { tier, billing, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout"); setAction(""); }
  };

  const openPortal = async () => {
    setAction("portal");
    try {
      const { data } = await api.post("/attorney/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal"); setAction(""); }
  };

  const saveProfile = async () => {
    try {
      const payload = { name: form.name, firm: form.firm, phone: form.phone, states: form.states || [],
        practice_areas: form.practice_areas || [], years_experience: form.years_experience ? Number(form.years_experience) : null,
        website: form.website, bio: form.bio };
      const { data } = await api.put("/attorney/profile", payload);
      setAtt(data); setForm(data); setEditing(false);
      toast.success("Profile updated");
    } catch { toast.error("Could not save profile"); }
  };

  const markContacted = async (id) => {
    try { await api.post(`/attorney/leads/${id}/contacted`); setLeads((ls) => ls.map((l) => l.lead_id === id ? { ...l, contacted: true } : l)); }
    catch { toast.error("Could not update"); }
  };

  const toggle = (key, v) => setForm((f) => ({ ...f, [key]: (f[key] || []).includes(v) ? f[key].filter((x) => x !== v) : [...(f[key] || []), v] }));
  const tier = att?.tier || "basic";

  if (busy || !att) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-forest-700" /></div>;

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Attorney dashboard</span>
            <h1 className="mt-2 font-serif text-4xl font-semibold text-forest-900 flex items-center gap-3">
              {att.name}
              {tier === "elite" && <span className="inline-flex items-center gap-1 rounded-full bg-forest-800 text-parchment-50 text-xs px-2.5 py-1"><BadgeCheck className="h-3.5 w-3.5" /> Elite</span>}
              {tier === "pro" && <span className="inline-flex items-center gap-1 rounded-full bg-terracotta text-white text-xs px-2.5 py-1"><Star className="h-3.5 w-3.5" /> Pro</span>}
            </h1>
            <p className="mt-1 text-ink-600">{tier === "none" ? "No active plan — choose one below to get listed" : <>Current plan: <strong className="capitalize">{tier}</strong>{att.subscription_status ? ` · ${att.subscription_status}` : ""}</>}</p>
          </div>
          {att.stripe_customer_id && (
            <Button onClick={openPortal} data-testid="billing-portal-btn" variant="outline" disabled={action === "portal"} className="rounded-full border-parchment-200 gap-2">
              {action === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Manage billing
            </Button>
          )}
        </div>

        {tier === "none" && (
          <div data-testid="inactive-banner" className="mt-6 rounded-2xl border-l-4 border-terracotta bg-terracotta-subtle p-4">
            <p className="font-semibold text-[#8C3415]">Your listing isn't live yet</p>
            <p className="text-sm text-[#8C3415]">Choose a membership below ($50/mo and up) to appear in the directory and start receiving client leads.</p>
          </div>
        )}

        <Tabs defaultValue="membership" className="mt-8">
          <TabsList className="bg-parchment-100 border border-parchment-200 rounded-full p-1 h-auto">
            <TabsTrigger value="membership" data-testid="tab-membership" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Sparkles className="h-4 w-4" /> Membership</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Inbox className="h-4 w-4" /> Leads
              {unread > 0 && <span data-testid="unread-badge" className="ml-1 grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-terracotta text-white text-[10px] font-bold">{unread}</span>}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Settings className="h-4 w-4" /> Profile</TabsTrigger>
          </TabsList>

          {/* MEMBERSHIP */}
          <TabsContent value="membership" className="mt-6">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center rounded-full border border-parchment-200 bg-white p-1">
                <button onClick={() => setBilling("monthly")} data-testid="billing-monthly" className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${billing === "monthly" ? "bg-forest-800 text-parchment-50" : "text-ink-600"}`}>Monthly</button>
                <button onClick={() => setBilling("annual")} data-testid="billing-annual" className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${billing === "annual" ? "bg-forest-800 text-parchment-50" : "text-ink-600"}`}>Annual <span className="text-terracotta font-semibold">· 2 months free</span></button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {PLANS.map((p) => {
                const current = tier === p.tier;
                const isPaid = p.monthly > 0;
                const priceNum = billing === "annual" ? p.annual : p.monthly;
                return (
                  <motion.div key={p.tier} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className={`rounded-3xl border p-6 flex flex-col ${p.tier === "elite" ? "border-forest-800 ring-1 ring-forest-800/10" : p.tier === "pro" ? "border-terracotta/40" : "border-parchment-200"} bg-white`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-2xl font-semibold text-forest-900">{p.name}</h3>
                      {current && <span className="text-xs font-semibold rounded-full bg-forest-100 text-forest-800 px-2.5 py-1">Current</span>}
                    </div>
                    <div className="mt-2 flex items-end gap-1">
                      <span className="font-serif text-4xl font-semibold text-forest-900">{isPaid ? `$${priceNum.toLocaleString()}` : "Free"}</span>
                      {isPaid && <span className="text-ink-500 mb-1">{billing === "annual" ? "/yr" : "/mo"}</span>}
                    </div>
                    {isPaid && billing === "annual" && <p className="text-xs text-terracotta font-medium mt-0.5">Save ${(p.monthly * 12 - p.annual).toLocaleString()} vs monthly</p>}
                    <ul className="mt-4 space-y-2 flex-1">
                      {p.perks.map((k) => <li key={k} className="flex items-start gap-2 text-sm text-ink-700"><Check className="h-4 w-4 text-forest-700 mt-0.5 shrink-0" />{k}</li>)}
                    </ul>
                    {current ? (
                      <Button onClick={openPortal} variant="outline" className="mt-5 rounded-full border-parchment-200 gap-2"><CreditCard className="h-4 w-4" /> Manage</Button>
                    ) : (
                      <Button onClick={() => subscribe(p.tier)} data-testid={`subscribe-${p.tier}`} disabled={action === p.tier}
                        className={`mt-5 rounded-full gap-2 ${p.tier === "elite" ? "bg-forest-800 hover:bg-forest-900 text-parchment-50" : "bg-terracotta hover:bg-terracotta-hover text-white"}`}>
                        {action === p.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Choose {p.name} <ArrowUpRight className="h-4 w-4" /></>}
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-ink-400 text-center">Recurring {billing} subscription via Stripe. Cancel anytime from "Manage billing". Test card 4242 4242 4242 4242.</p>
          </TabsContent>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            {leads.length === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-parchment-300 bg-white p-10 text-center">
                <Inbox className="h-8 w-8 text-ink-400 mx-auto" />
                <p className="mt-3 text-ink-500">No client leads yet. Upgrade to Pro or Elite to appear higher and get seen first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.lead_id} data-testid={`att-lead-${l.lead_id}`} className={`rounded-2xl border bg-white p-5 ${l.contacted ? "border-parchment-200 opacity-70" : "border-forest-600/30"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-forest-900">{l.name}</h3>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-600">
                          <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-terracotta"><Mail className="h-3.5 w-3.5" />{l.email}</a>
                          {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-terracotta"><Phone className="h-3.5 w-3.5" />{l.phone}</a>}
                          {l.state && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.state}</span>}
                          {l.charge && <span>{l.charge}</span>}
                        </div>
                        {l.message && <p className="mt-2 text-sm text-ink-700">{l.message}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-xs text-ink-400 block">{new Date(l.created_at).toLocaleDateString()}</span>
                        {l.contacted ? <span className="mt-2 inline-flex items-center gap-1 text-xs text-forest-700"><Check className="h-3.5 w-3.5" /> Contacted</span>
                          : <Button onClick={() => markContacted(l.lead_id)} data-testid={`contacted-${l.lead_id}`} size="sm" variant="outline" className="mt-2 rounded-full border-parchment-200 gap-1"><PhoneCall className="h-3.5 w-3.5" /> Mark contacted</Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-6">
            <div className="rounded-3xl border border-parchment-200 bg-white p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl font-semibold text-forest-900">Your public profile</h3>
                {!editing ? <Button onClick={() => setEditing(true)} data-testid="edit-profile-btn" variant="outline" className="rounded-full border-parchment-200 gap-2"><Pencil className="h-4 w-4" /> Edit</Button>
                  : <Button onClick={saveProfile} data-testid="save-profile-btn" className="rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2"><Save className="h-4 w-4" /> Save</Button>}
              </div>
              {!editing ? (
                <div className="space-y-2 text-ink-700">
                  <p><strong>{att.firm || "—"}</strong></p>
                  <p className="text-sm">{(att.states || []).join(", ") || "No states set"}</p>
                  <div className="flex flex-wrap gap-1.5">{(att.practice_areas || []).map((p) => <span key={p} className="rounded-full bg-parchment-100 text-ink-600 text-xs px-2.5 py-1">{p}</span>)}</div>
                  <p className="text-sm text-ink-600 pt-2">{att.bio || "No bio yet."}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="mb-1 block">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-parchment-200" /></div>
                    <div><Label className="mb-1 block">Firm</Label><Input value={form.firm || ""} onChange={(e) => setForm({ ...form, firm: e.target.value })} className="rounded-xl border-parchment-200" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="mb-1 block">Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border-parchment-200" /></div>
                    <div><Label className="mb-1 block">Website</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-xl border-parchment-200" /></div>
                  </div>
                  <div>
                    <Label className="mb-2 block">States</Label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto thin-scroll p-1">
                      {STATES.map((s) => <button type="button" key={s.abbr} onClick={() => toggle("states", s.name)} className={`rounded-full px-3 py-1 text-xs border ${(form.states || []).includes(s.name) ? "bg-forest-800 text-parchment-50 border-forest-800" : "bg-white border-parchment-200 text-ink-600"}`}>{s.name}</button>)}
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block">Practice areas</Label>
                    <div className="flex flex-wrap gap-2">
                      {CHARGE_TYPES.map((c) => <button type="button" key={c} onClick={() => toggle("practice_areas", c)} className={`rounded-full px-3 py-1 text-xs border ${(form.practice_areas || []).includes(c) ? "bg-terracotta text-white border-terracotta" : "bg-white border-parchment-200 text-ink-600"}`}>{c}</button>)}
                    </div>
                  </div>
                  <div><Label className="mb-1 block">Bio</Label><Textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="rounded-xl border-parchment-200 resize-none" /></div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
